import React, { useState, useEffect, useRef } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import {
  FiSearch,
  FiPlus,
  FiCheck,
  FiX,
  FiRefreshCw,
  FiShield,
  FiTrash2,
  FiChevronDown,
  FiEdit,
  FiEye,
  FiLock,
  FiKey,
  FiUsers,
  FiZap,
  FiLayers,
  FiClipboard,
} from "react-icons/fi";
import { toast } from "react-toastify";
import "../index.css";
import "./EmployeeDashboard.css";
import "./EmployeeLeaves.css";

// =============================================
// API CONFIGURATION
// =============================================
const EMPLOYEE_API_URL = "https://api.timelyhealth.in/api/employees/get-employees";
const ACCESS_API_URL = "https://ingrainhirebackend.ingrainsystems.com/api/candidate";

// =============================================
// DYNAMIC PAGES FROM ADMINLAYOUT MENU
// =============================================
const getPagesFromAdminLayout = () => {
  const menuSections = [
    {
      title: "Core",
      items: [
        { path: "/recruitment-dashboard", label: "Dashboard", icon: "📊" },
      ],
    },
    {
      title: "Job Management",
      items: [
        { path: "/job-post", label: "All Jobs", icon: "📁" },
        { path: "/post-new-job", label: "Post New Job", icon: "➕" },
        { path: "/job-positions-status", label: "Job Status", icon: "📈" },
        { path: "/job-applicants", label: "Job Applicants", icon: "👥" },
        { path: "/score", label: "Scores", icon: "🏆" },
      ],
    },
    {
      title: "Short Jobs",
      items: [
        { path: "/short-jobs", label: "Post Short Job", icon: "⭐" },
        { path: "/posted-short-jobs", label: "Posted Short Jobs", icon: "📋" },
        { path: "/short-job-applicants", label: "Short Job Applicants", icon: "👤" },
      ],
    },
    {
      title: "Assessments & Docs",
      items: [
        { path: "/assessment-manager", label: "All Assessments", icon: "📋" },
        { path: "/new-assessment", label: "Create Assessment", icon: "📄" },
        { path: "/personal-documents", label: "Personal Docs", icon: "📄" },
        { path: "/all-medical-certificates", label: "Medical Certificates", icon: "❤️" },
      ],
    },
    {
      title: "Communications",
      items: [
        { path: "/acceptance-letter", label: "Acceptance Letters", icon: "📧" },
        { path: "/employee-journey", label: "Employee Journey", icon: "🧭" },
      ],
    },
    {
      title: "Wallet & Rewards",
      items: [
        { path: "/mywallet", label: "My Wallet", icon: "💳" },
        { path: "/myreffral", label: "Refer & Earn", icon: "📤" },
        { path: "/specialoffers", label: "Special Offers", icon: "🏷️" },
      ],
    },
    {
      title: "Access Control",
      items: [
        { path: "/page-access", label: "Page Access", icon: "🛡️" },
      ],
    },
  ];

  const pages = [];
  menuSections.forEach((section) => {
    if (section.items) {
      section.items.forEach((item) => {
        const id =
          item.path.replace("/", "").replace(/-/g, "_") ||
          item.label.toLowerCase().replace(/\s/g, "_");
        pages.push({
          id: id,
          name: item.label,
          path: item.path,
          icon: item.icon || "📄",
          category: section.title,
        });
      });
    }
  });

  return { pages, menuSections };
};


const PageAccess = () => {
  const context = useOutletContext();
  const outletEmployees = context?.employees || [];

  // =============================================
  // STATE MANAGEMENT
  // =============================================
  const [searchQuery, setSearchQuery] = useState("");
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [allEmployees, setAllEmployees] = useState([]);
  const [allAccessData, setAllAccessData] = useState([]);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupEmployee, setPopupEmployee] = useState(null);
  const [popupPages, setPopupPages] = useState([]);
  const [popupAccessId, setPopupAccessId] = useState(null);
  const [popupSearchQuery, setPopupSearchQuery] = useState("");
  const [popupPageSearch, setPopupPageSearch] = useState("");
  const [popupIsEmployeeOpen, setPopupIsEmployeeOpen] = useState(false);
  const [popupLoading, setPopupLoading] = useState(false);
  const [isViewPopupOpen, setIsViewPopupOpen] = useState(false);
  const [viewEmployeeData, setViewEmployeeData] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  const popupRef = useRef(null);
  const popupEmployeeRef = useRef(null);
  const viewPopupRef = useRef(null);

  const { pages: allPages, menuSections } = getPagesFromAdminLayout();

  const filteredAccessList = allAccessData.filter((item) => {
    const query = searchQuery.toLowerCase();
    const name = (item.employeeName || "").toLowerCase();
    const email = (item.employeeEmail || "").toLowerCase();
    const role = (item.employeeRole || "").toLowerCase();
    const dept = (item.employeeDepartment || "").toLowerCase();
    return name.includes(query) || email.includes(query) || role.includes(query) || dept.includes(query);
  });

  const popupFilteredEmployees = allEmployees.filter((emp) => {
    const query = popupSearchQuery.toLowerCase();
    const name = (emp.name || "").toLowerCase();
    const email = (emp.email || "").toLowerCase();
    const role = (emp.role || "").toLowerCase();
    const dept = (emp.department || "").toLowerCase();
    return name.includes(query) || email.includes(query) || role.includes(query) || dept.includes(query);
  });

  const popupFilteredPages = allPages.filter((page) => {
    const query = popupPageSearch.toLowerCase();
    const name = (page.name || "").toLowerCase();
    const cat = (page.category || "").toLowerCase();
    return name.includes(query) || cat.includes(query);
  });

  // =============================================
  // 1. GET ALL ACCESS BY CLIENT
  // =============================================
  const fetchAccess = async (clientId) => {
    try {
      const response = await axios.get(`${ACCESS_API_URL}/allpermission/${clientId}`);
      if (response.data?.success) {
        const data = response.data.data || [];
        setAllAccessData(data);
        return data;
      }
      return [];
    } catch (error) {
      console.error("❌ Error fetching access:", error);
      toast.error("Failed to fetch access permissions");
      return [];
    }
  };

  // =============================================
  // 2. ADD ACCESS
  // =============================================
  const addAccess = async (clientId, employeeData, pages) => {
    const response = await axios.post(`${ACCESS_API_URL}/addpermission`, {
      clientId,
      employeeId: employeeData._id,
      employeeName: employeeData.name,
      employeeEmail: employeeData.email,
      employeeRole: employeeData.role || "Employee",
      employeeDepartment: employeeData.department || "",
      employeeProfileImage: employeeData.profileImage || null,
      pages,
    });
    return response.data;
  };

  // =============================================
  // 3. UPDATE ACCESS
  // =============================================
  const updateAccess = async (id, employeeData, pages) => {
    const response = await axios.put(`${ACCESS_API_URL}/updatepermission/${id}`, {
      employeeName: employeeData.name,
      employeeEmail: employeeData.email,
      employeeRole: employeeData.role || "Employee",
      employeeDepartment: employeeData.department || "",
      employeeProfileImage: employeeData.profileImage || null,
      pages,
    });
    return response.data;
  };

  // =============================================
  // 4. DELETE ACCESS
  // =============================================
  const deleteAccess = async (id) => {
    try {
      const response = await axios.delete(`${ACCESS_API_URL}/deletepermission/${id}`);
      return response.data;
    } catch (error) {
      console.error("❌ Error deleting access:", error);
      throw error;
    }
  };

  // =============================================
  // FETCH EMPLOYEES
  // =============================================
  const fetchEmployees = async () => {
    setLoadingEmployees(true);
    try {
      const response = await axios.get(EMPLOYEE_API_URL);

      let employees = [];
      if (Array.isArray(response.data)) {
        employees = response.data
          .filter((emp) => emp.status === "active")
          .map((emp) => ({
            _id: emp._id,
            name: emp.name || `${emp.firstName || ""} ${emp.lastName || ""}`.trim() || "Unnamed",
            email: emp.email || "No email",
            role: emp.role || "Employee",
            department: emp.department || "",
            employeeId: emp.employeeId || "",
            status: emp.status || "active",
            profileImage: emp.profileImage || null,
            ...emp,
          }));
      } else if (outletEmployees.length > 0) {
        employees = outletEmployees.filter((emp) => emp.status === "active");
      }

      setAllEmployees(employees);

      const clientId = localStorage.getItem("clientId") || localStorage.getItem("clientCustomId") || "";
      if (clientId) {
        await fetchAccess(clientId);
      }
    } catch (error) {
      console.error("❌ Error fetching employees:", error);
      if (outletEmployees.length > 0) {
        setAllEmployees(outletEmployees.filter((emp) => emp.status === "active"));
      } else {
        toast.error("Could not load employee directory");
      }
    } finally {
      setLoadingEmployees(false);
    }
  };

  const getPageName = (pageId) => {
    const page = allPages.find((p) => p.id === pageId);
    return page ? page.name : pageId;
  };

  const getPageIcon = (pageId) => {
    const page = allPages.find((p) => p.id === pageId);
    return page ? page.icon : "📄";
  };

  const getInitial = (name) => {
    if (!name) return "U";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // =============================================
  // POPUP FUNCTIONS
  // =============================================
  const openPopup = (employee = null) => {
    if (employee) {
      const access = allAccessData.find((a) => a.employeeId === employee._id);
      setPopupEmployee(employee);
      setPopupPages(access ? access.pages || [] : []);
      setPopupAccessId(access ? access._id : null);
    } else {
      setPopupEmployee(null);
      setPopupPages([]);
      setPopupAccessId(null);
    }
    setPopupSearchQuery("");
    setPopupPageSearch("");
    setPopupIsEmployeeOpen(false);
    setIsPopupOpen(true);
  };

  const closePopup = () => {
    setIsPopupOpen(false);
    setPopupEmployee(null);
    setPopupPages([]);
    setPopupAccessId(null);
    setPopupSearchQuery("");
    setPopupPageSearch("");
  };

  const handlePopupEmployeeSelect = (employee) => {
    setPopupEmployee(employee);
    setPopupIsEmployeeOpen(false);

    const access = allAccessData.find((a) => a.employeeId === employee._id);
    if (access) {
      setPopupAccessId(access._id);
      setPopupPages(access.pages || []);
    } else {
      setPopupAccessId(null);
      setPopupPages([]);
    }
  };

  const togglePopupPageSelection = (pageId) => {
    setPopupPages((prev) => {
      if (prev.includes(pageId)) {
        return prev.filter((id) => id !== pageId);
      } else {
        return [...prev, pageId];
      }
    });
  };

  const selectAllCategoryPages = (categoryItems) => {
    const catIds = categoryItems.map((item) => {
      return item.id || item.path.replace("/", "").replace(/-/g, "_");
    });
    setPopupPages((prev) => Array.from(new Set([...prev, ...catIds])));
  };

  const deselectCategoryPages = (categoryItems) => {
    const catIds = new Set(categoryItems.map((item) => {
      return item.id || item.path.replace("/", "").replace(/-/g, "_");
    }));
    setPopupPages((prev) => prev.filter((id) => !catIds.has(id)));
  };

  const selectAllPopupPages = () => {
    const allPageIds = allPages.map((p) => p.id);
    setPopupPages(allPageIds);
  };

  const deselectAllPopupPages = () => {
    setPopupPages([]);
  };

  const savePopupAccess = async () => {
    if (!popupEmployee) {
      toast.error("Please select an employee");
      return;
    }

    if (popupPages.length === 0) {
      toast.error("Please select at least one page permission");
      return;
    }

    setPopupLoading(true);
    try {
      const clientId = localStorage.getItem("clientId") || localStorage.getItem("clientCustomId") || "";

      const employeeData = {
        _id: popupEmployee._id,
        name: popupEmployee.name,
        email: popupEmployee.email,
        role: popupEmployee.role || "Employee",
        department: popupEmployee.department || "",
        profileImage: popupEmployee.profileImage || null,
      };

      let response;
      if (popupAccessId) {
        response = await updateAccess(popupAccessId, employeeData, popupPages);
      } else {
        response = await addAccess(clientId, employeeData, popupPages);
      }

      if (response?.success) {
        const msg = `Access permissions ${popupAccessId ? "updated" : "assigned"} for ${popupEmployee.name}!`;
        toast.success(msg);
        await fetchAccess(clientId);
        closePopup();
      } else {
        toast.error(response?.message || "Failed to save permissions");
      }
    } catch (error) {
      console.error("❌ Error saving access:", error);
      toast.error(error.response?.data?.message || "Failed to save access permissions.");
    } finally {
      setPopupLoading(false);
    }
  };

  const handleDeleteAccess = async (access) => {
    try {
      await deleteAccess(access._id);
      toast.success(`Access removed for ${access.employeeName}`);
      const clientId = localStorage.getItem("clientId") || localStorage.getItem("clientCustomId") || "";
      await fetchAccess(clientId);
      setDeleteConfirmId(null);
    } catch (err) {
      toast.error("Failed to delete permission record");
    }
  };

  const openViewPopup = (access) => {
    const emp = allEmployees.find((e) => e._id === access.employeeId);
    setViewEmployeeData({
      ...access,
      employee: emp,
    });
    setIsViewPopupOpen(true);
  };

  const closeViewPopup = () => {
    setIsViewPopupOpen(false);
    setViewEmployeeData(null);
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupEmployeeRef.current && !popupEmployeeRef.current.contains(event.target)) {
        setPopupIsEmployeeOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const totalAssignedPagesCount = allAccessData.reduce((acc, curr) => acc + (curr.pages?.length || 0), 0);

  return (
    <div style={{ backgroundColor: "white" }} className="emp-dash">
      <main>
        {/* Page Header */}
        <div className="emp-dash__header">
          <div>
            <h1 className="emp-dash__greeting">
              Page <span>Access</span>
            </h1>
            <p className="emp-dash__subtitle">
              Manage role-based page permissions and module access for your team members.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="emp-dash__date-pill">
              <FiShield />
              <span>
                {new Date().toLocaleDateString("en-US", {
                  weekday: "short",
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })}
              </span>
            </div>
            <button
              type="button"
              onClick={() => openPopup()}
              className="flex items-center gap-2 py-2.5 px-4 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold uppercase tracking-wider transition-all shadow-lg shadow-indigo-600/20 cursor-pointer"
            >
              <FiPlus size={15} /> Grant Access
            </button>
          </div>
        </div>

        {/* Stats Strip */}
        <div className="emp-dash__stats grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="emp-dash__stat">
            <div className="emp-dash__stat-top">
              <span className="emp-dash__stat-label">Active Permissions</span>
              <div className="emp-dash__stat-icon emp-dash__stat-icon--indigo">
                <FiShield />
              </div>
            </div>
            <div className="emp-dash__stat-value">{allAccessData.length}</div>
            <div className="emp-dash__stat-meta">authorized employees</div>
          </div>

          <div className="emp-dash__stat">
            <div className="emp-dash__stat-top">
              <span className="emp-dash__stat-label">Team Members</span>
              <div className="emp-dash__stat-icon emp-dash__stat-icon--violet">
                <FiUsers />
              </div>
            </div>
            <div className="emp-dash__stat-value">{allEmployees.length}</div>
            <div className="emp-dash__stat-meta">active employees in system</div>
          </div>

          <div className="emp-dash__stat">
            <div className="emp-dash__stat-top">
              <span className="emp-dash__stat-label">Available Pages</span>
              <div className="emp-dash__stat-icon emp-dash__stat-icon--emerald">
                <FiLayers />
              </div>
            </div>
            <div className="emp-dash__stat-value">{allPages.length}</div>
            <div className="emp-dash__stat-meta">protected app modules</div>
          </div>

          <div className="emp-dash__stat">
            <div className="emp-dash__stat-top">
              <span className="emp-dash__stat-label">Granted Grants</span>
              <div className="emp-dash__stat-icon emp-dash__stat-icon--rose">
                <FiKey />
              </div>
            </div>
            <div className="emp-dash__stat-value">{totalAssignedPagesCount}</div>
            <div className="emp-dash__stat-meta">total assigned page rights</div>
          </div>
        </div>

        {/* Main Permissions Directory Card */}
        <div className="emp-dash__card p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
                <FiLock size={15} className="text-indigo-600" />
              </div>
              <div>
                <h2 className="text-sm font-bold text-gray-800 uppercase tracking-widest">
                  Team Permissions Directory
                </h2>
                <p className="text-[10px] text-gray-500">
                  Showing {filteredAccessList.length} of {allAccessData.length} records
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={async () => {
                  const clientId = localStorage.getItem("clientId") || localStorage.getItem("clientCustomId") || "";
                  await fetchAccess(clientId);
                  toast.success("Permissions refreshed!");
                }}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-gray-50 text-gray-600 hover:bg-indigo-50 hover:text-indigo-600 text-[10px] font-bold uppercase tracking-widest border border-gray-200 transition-all cursor-pointer"
                title="Sync database records"
              >
                <FiRefreshCw size={12} className={loadingEmployees ? "animate-spin" : ""} />
                Sync
              </button>
            </div>
          </div>

          {/* Search Bar */}
          <div className="relative mb-5">
            <FiSearch
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-indigo-600 transition-colors z-10"
              size={14}
            />
            <input
              type="text"
              placeholder="Search by employee name, email, department, or role..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-11 pr-4 py-2.5 bg-gray-50/50 rounded-xl border border-gray-200 focus:bg-white focus:ring-4 focus:ring-indigo-600/5 focus:border-indigo-600 transition-all outline-none text-xs text-gray-800"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <FiX size={14} />
              </button>
            )}
          </div>

          {/* Permissions Table */}
          <div className="overflow-x-auto rounded-xl border border-gray-200/80">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="bg-gray-50/90 border-b border-gray-200 text-gray-600 uppercase text-[10px] tracking-wider font-bold">
                  <th className="py-3 px-4 w-12 text-center">S.No</th>
                  <th className="py-3 px-4">Employee</th>
                  <th className="py-3 px-4">Role</th>
                  <th className="py-3 px-4">Department</th>
                  <th className="py-3 px-4">Accessible Modules</th>
                  <th className="py-3 px-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loadingEmployees ? (
                  <tr>
                    <td colSpan="6" className="py-12 text-center text-gray-400">
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-6 h-6 border-2 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
                        <span className="text-xs font-medium">Syncing permissions...</span>
                      </div>
                    </td>
                  </tr>
                ) : filteredAccessList.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="py-12 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <div className="w-12 h-12 rounded-xl bg-gray-50 text-gray-300 flex items-center justify-center mb-2">
                          <FiShield size={24} />
                        </div>
                        <p className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                          No Permissions Found
                        </p>
                        <p className="text-[10px] text-gray-400 mt-1">
                          {searchQuery
                            ? "Try matching different keywords."
                            : "Click 'Grant Access' above to assign page permissions."}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredAccessList.map((access, index) => {
                    const pages = access.pages || [];
                    return (
                      <tr
                        key={access._id || index}
                        className="hover:bg-indigo-50/20 transition-colors"
                      >
                        <td className="py-3.5 px-4 text-center text-gray-400 font-medium text-[11px]">
                          {index + 1}
                        </td>
                        <td className="py-3.5 px-4">
                          <div className="flex items-center gap-3">
                            <div
                              className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0 shadow-sm"
                              style={{
                                background: access.employeeProfileImage
                                  ? `url(${access.employeeProfileImage}) center/cover`
                                  : "linear-gradient(135deg, #4f46e5, #7c3aed)",
                              }}
                            >
                              {!access.employeeProfileImage && getInitial(access.employeeName)}
                            </div>
                            <div>
                              <p className="font-bold text-gray-900 text-xs">
                                {access.employeeName}
                              </p>
                              <p className="text-[10px] text-gray-500 font-medium">
                                {access.employeeEmail}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3.5 px-4">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-indigo-50 text-indigo-700 border border-indigo-100">
                            {access.employeeRole || "Employee"}
                          </span>
                        </td>
                        <td className="py-3.5 px-4 text-gray-600 font-medium">
                          {access.employeeDepartment || "General"}
                        </td>
                        <td className="py-3.5 px-4">
                          <div className="flex flex-wrap items-center gap-1.5 max-w-md">
                            {pages.length > 0 ? (
                              <>
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-bold bg-gray-50 border border-gray-200 text-gray-800">
                                  <span>{getPageIcon(pages[0])}</span>
                                  {getPageName(pages[0])}
                                </span>
                                {pages.length > 1 && (
                                  <span
                                    onClick={() => openViewPopup(access)}
                                    className="cursor-pointer inline-flex items-center px-2 py-1 rounded-lg text-[10px] font-bold bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-100 transition-colors"
                                  >
                                    +{pages.length - 1} more
                                  </span>
                                )}
                              </>
                            ) : (
                              <span className="text-[10px] text-gray-400 italic">
                                No pages assigned
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-3.5 px-4">
                          <div className="flex items-center justify-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => openViewPopup(access)}
                              className="p-1.5 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors cursor-pointer"
                              title="View Details"
                            >
                              <FiEye size={14} />
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                const emp = allEmployees.find((e) => e._id === access.employeeId) || {
                                  _id: access.employeeId,
                                  name: access.employeeName,
                                  email: access.employeeEmail,
                                  role: access.employeeRole,
                                  department: access.employeeDepartment,
                                };
                                openPopup(emp);
                              }}
                              className="p-1.5 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors cursor-pointer"
                              title="Edit Access"
                            >
                              <FiEdit size={14} />
                            </button>
                            <button
                              type="button"
                              onClick={() => setDeleteConfirmId(access._id)}
                              className="p-1.5 text-gray-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
                              title="Revoke Access"
                            >
                              <FiTrash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Quick Tip / Security Notice */}
        <div className="p-4 bg-amber-50 rounded-2xl border border-amber-100 flex items-start gap-3">
          <FiZap size={16} className="text-amber-500 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="text-xs font-bold text-amber-900 uppercase tracking-wider mb-1">
              Role Access & Security Policy
            </h4>
            <p className="text-[11px] text-amber-800 leading-relaxed">
              Assigned pages determine sidebar navigation links and route guards. Employees will only see authorized menu modules upon login. Changes take effect in real-time.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-center gap-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest py-8">
          <FiClipboard className="text-indigo-500" />
          Last Updated: {new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
        </div>
      </main>

      {/* ============================================= */}
      {/* GRANT / EDIT ACCESS MODAL */}
      {/* ============================================= */}
      {isPopupOpen && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md">
          <div
            ref={popupRef}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden border border-gray-100 animate-in fade-in zoom-in-95 duration-200"
          >
            {/* Modal Header */}
            <div className="px-6 py-5 bg-gradient-to-r from-indigo-50 via-white to-violet-50 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center shadow-md shadow-indigo-600/20">
                  <FiShield size={18} />
                </div>
                <div>
                  <h2 className="text-base font-bold text-gray-900">
                    {popupAccessId ? "Edit Page Access" : "Grant Page Access"}
                  </h2>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-indigo-600">
                    {popupAccessId ? "Update Employee Permissions" : "Assign Modules to Employee"}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={closePopup}
                className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-500 flex items-center justify-center transition-colors cursor-pointer"
              >
                <FiX size={16} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-5 overflow-y-auto flex-1 custom-scrollbar">
              {/* Select Employee */}
              <div className="space-y-1.5">
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-widest">
                  Select Employee <span className="text-rose-500">*</span>
                </label>
                <div className="relative" ref={popupEmployeeRef}>
                  <button
                    type="button"
                    onClick={() => setPopupIsEmployeeOpen((prev) => !prev)}
                    className="w-full px-4 py-3 text-left bg-white border border-gray-200 rounded-xl hover:border-indigo-500 focus:border-indigo-600 focus:ring-4 focus:ring-indigo-600/5 transition-all flex items-center justify-between"
                  >
                    <span className="flex items-center gap-3 min-w-0">
                      {popupEmployee ? (
                        <>
                          <div
                            className="w-7 h-7 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0"
                            style={{
                              background: popupEmployee.profileImage
                                ? `url(${popupEmployee.profileImage}) center/cover`
                                : "linear-gradient(135deg, #4f46e5, #7c3aed)",
                            }}
                          >
                            {!popupEmployee.profileImage && getInitial(popupEmployee.name)}
                          </div>
                          <span className="truncate">
                            <span className="font-bold text-gray-900 text-xs">{popupEmployee.name}</span>
                            <span className="text-[11px] text-gray-500 ml-2 font-medium">
                              ({popupEmployee.role || "Employee"} · {popupEmployee.email})
                            </span>
                          </span>
                        </>
                      ) : (
                        <span className="text-xs text-gray-400 font-medium">Search and select an active employee...</span>
                      )}
                    </span>
                    <FiChevronDown
                      className={`text-gray-400 transition-transform ${
                        popupIsEmployeeOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {popupIsEmployeeOpen && (
                    <div className="absolute left-0 z-50 mt-1.5 w-full bg-white border border-gray-200 rounded-xl shadow-xl max-h-56 overflow-y-auto">
                      <div className="p-2 border-b border-gray-100 sticky top-0 bg-white z-10">
                        <div className="relative">
                          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                          <input
                            type="text"
                            placeholder="Type employee name or email..."
                            value={popupSearchQuery}
                            onChange={(e) => setPopupSearchQuery(e.target.value)}
                            className="w-full py-1.5 pl-8 pr-3 text-xs bg-gray-50 border border-gray-200 rounded-lg outline-none focus:bg-white focus:border-indigo-600"
                            autoFocus
                          />
                        </div>
                      </div>
                      <div className="p-1">
                        {popupFilteredEmployees.length === 0 ? (
                          <div className="p-3 text-center text-xs text-gray-400">
                            No employees match your search
                          </div>
                        ) : (
                          popupFilteredEmployees.map((emp) => (
                            <div
                              key={emp._id}
                              onClick={() => handlePopupEmployeeSelect(emp)}
                              className={`p-2.5 rounded-lg cursor-pointer flex items-center justify-between text-xs transition-colors ${
                                popupEmployee?._id === emp._id
                                  ? "bg-indigo-50 text-indigo-700 font-bold"
                                  : "hover:bg-gray-50 text-gray-700"
                              }`}
                            >
                              <div className="flex items-center gap-2.5 min-w-0">
                                <div
                                  className="w-6 h-6 rounded-full flex items-center justify-center text-white font-bold text-[10px] flex-shrink-0"
                                  style={{
                                    background: emp.profileImage
                                      ? `url(${emp.profileImage}) center/cover`
                                      : "linear-gradient(135deg, #4f46e5, #7c3aed)",
                                  }}
                                >
                                  {!emp.profileImage && getInitial(emp.name)}
                                </div>
                                <div className="truncate">
                                  <p className="font-bold text-gray-800 truncate">{emp.name}</p>
                                  <p className="text-[10px] text-gray-400 truncate">{emp.role} · {emp.email}</p>
                                </div>
                              </div>
                              {popupEmployee?._id === emp._id && <FiCheck className="text-indigo-600 flex-shrink-0" />}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Page Permissions Checklist */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-widest">
                    Assign Page Permissions <span className="text-rose-500">*</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={selectAllPopupPages}
                      className="text-[10px] font-bold text-indigo-600 hover:underline uppercase tracking-wider"
                    >
                      Select All
                    </button>
                    <span className="text-gray-300">•</span>
                    <button
                      type="button"
                      onClick={deselectAllPopupPages}
                      className="text-[10px] font-bold text-gray-500 hover:underline uppercase tracking-wider"
                    >
                      Clear All
                    </button>
                    <span className="ml-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">
                      {popupPages.length} Selected
                    </span>
                  </div>
                </div>

                {/* Grouped Accordions / Categories */}
                <div className="space-y-3 pt-1">
                  {menuSections.map((section, idx) => {
                    const sectionItems = section.items || [];
                    const selectedCount = sectionItems.filter((item) => {
                      const id = item.path.replace("/", "").replace(/-/g, "_");
                      return popupPages.includes(id);
                    }).length;
                    const isAllSectionSelected = selectedCount === sectionItems.length && sectionItems.length > 0;

                    return (
                      <div
                        key={idx}
                        className="rounded-xl border border-gray-200/90 overflow-hidden bg-white"
                      >
                        {/* Section Header */}
                        <div className="px-3.5 py-2.5 bg-gray-50/80 border-b border-gray-100 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-gray-800 uppercase tracking-wider">
                              {section.title}
                            </span>
                            <span className="text-[10px] text-gray-400 font-medium">
                              ({selectedCount}/{sectionItems.length})
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              if (isAllSectionSelected) {
                                deselectCategoryPages(sectionItems);
                              } else {
                                selectAllCategoryPages(sectionItems);
                              }
                            }}
                            className="text-[10px] font-bold text-indigo-600 hover:text-indigo-800"
                          >
                            {isAllSectionSelected ? "Deselect Section" : "Select Section"}
                          </button>
                        </div>

                        {/* Items Grid */}
                        <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {sectionItems.map((item) => {
                            const id = item.path.replace("/", "").replace(/-/g, "_");
                            const isSelected = popupPages.includes(id);
                            return (
                              <label
                                key={id}
                                className={`flex items-center gap-2.5 p-2.5 rounded-xl border cursor-pointer transition-all ${
                                  isSelected
                                    ? "bg-indigo-50/60 border-indigo-300 text-indigo-900 font-bold"
                                    : "bg-white border-gray-200/70 hover:border-gray-300 text-gray-700"
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  className="hidden"
                                  checked={isSelected}
                                  onChange={() => togglePopupPageSelection(id)}
                                />
                                <div
                                  className={`w-4 h-4 rounded-md border flex items-center justify-center flex-shrink-0 transition-colors ${
                                    isSelected
                                      ? "bg-indigo-600 border-indigo-600 text-white"
                                      : "bg-white border-gray-300"
                                  }`}
                                >
                                  {isSelected && <FiCheck size={11} strokeWidth={3} />}
                                </div>
                                <span className="text-sm">{item.icon}</span>
                                <span className="text-xs truncate">{item.label}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={closePopup}
                className="py-2.5 px-5 rounded-xl border border-gray-200 text-xs font-bold uppercase tracking-wider text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={savePopupAccess}
                disabled={popupLoading}
                className="py-2.5 px-6 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold uppercase tracking-wider transition-all shadow-lg shadow-indigo-600/20 disabled:opacity-50 flex items-center gap-2 cursor-pointer"
              >
                {popupLoading ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Saving Access...
                  </>
                ) : (
                  <>
                    <FiCheck size={14} />
                    {popupAccessId ? "Update Permissions" : "Save Permissions"}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================= */}
      {/* VIEW ACCESS DETAILS MODAL */}
      {/* ============================================= */}
      {isViewPopupOpen && viewEmployeeData && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md">
          <div
            ref={viewPopupRef}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden border border-gray-100 animate-in fade-in zoom-in-95 duration-200"
          >
            {/* Header */}
            <div className="px-6 py-5 bg-gradient-to-r from-indigo-50 via-white to-violet-50 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-sm shadow-md shadow-indigo-600/20 flex-shrink-0"
                  style={{
                    background: viewEmployeeData.employeeProfileImage
                      ? `url(${viewEmployeeData.employeeProfileImage}) center/cover`
                      : "linear-gradient(135deg, #4f46e5, #7c3aed)",
                  }}
                >
                  {!viewEmployeeData.employeeProfileImage && getInitial(viewEmployeeData.employeeName)}
                </div>
                <div>
                  <h2 className="text-base font-bold text-gray-900">{viewEmployeeData.employeeName}</h2>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-indigo-600">
                    {viewEmployeeData.employeeRole || "Employee"} · {viewEmployeeData.employeeDepartment || "General"}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={closeViewPopup}
                className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-500 flex items-center justify-center transition-colors cursor-pointer"
              >
                <FiX size={16} />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-5 overflow-y-auto flex-1 custom-scrollbar">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-gray-50 rounded-xl border border-gray-100">
                  <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">Email Address</p>
                  <p className="text-xs font-bold text-gray-800 mt-0.5">{viewEmployeeData.employeeEmail}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-xl border border-gray-100">
                  <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">Access Assigned</p>
                  <p className="text-xs font-bold text-indigo-600 mt-0.5">
                    {viewEmployeeData.pages?.length || 0} Modules Granted
                  </p>
                </div>
              </div>

              {/* Granted Pages List */}
              <div className="space-y-2">
                <h3 className="text-xs font-bold text-gray-700 uppercase tracking-widest">
                  Granted Page Modules
                </h3>
                {viewEmployeeData.pages && viewEmployeeData.pages.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {viewEmployeeData.pages.map((pageId) => (
                      <div
                        key={pageId}
                        className="p-2.5 rounded-xl bg-indigo-50/40 border border-indigo-100 flex items-center gap-2.5"
                      >
                        <span className="text-sm">{getPageIcon(pageId)}</span>
                        <div className="truncate">
                          <p className="text-xs font-bold text-gray-800 truncate">{getPageName(pageId)}</p>
                          <p className="text-[9px] text-gray-400 font-mono">/{pageId.replace(/_/g, "-")}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-6 text-center text-xs text-gray-400 bg-gray-50 rounded-xl">
                    No pages assigned to this user.
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
              <button
                type="button"
                onClick={() => {
                  closeViewPopup();
                  const emp = allEmployees.find((e) => e._id === viewEmployeeData.employeeId) || {
                    _id: viewEmployeeData.employeeId,
                    name: viewEmployeeData.employeeName,
                    email: viewEmployeeData.employeeEmail,
                    role: viewEmployeeData.employeeRole,
                    department: viewEmployeeData.employeeDepartment,
                  };
                  openPopup(emp);
                }}
                className="py-2 px-4 rounded-xl bg-indigo-50 text-indigo-700 hover:bg-indigo-100 text-xs font-bold uppercase tracking-wider transition-colors flex items-center gap-1.5"
              >
                <FiEdit size={13} /> Edit Permissions
              </button>
              <button
                type="button"
                onClick={closeViewPopup}
                className="py-2 px-5 rounded-xl border border-gray-200 text-xs font-bold uppercase tracking-wider text-gray-600 hover:bg-gray-100 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================= */}
      {/* DELETE CONFIRMATION MODAL */}
      {/* ============================================= */}
      {deleteConfirmId && (
        <div className="fixed inset-0 z-[350] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center space-y-4 border border-gray-100 animate-in fade-in zoom-in-95 duration-150">
            <div className="w-12 h-12 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center mx-auto">
              <FiTrash2 size={22} />
            </div>
            <div>
              <h3 className="text-sm font-bold text-gray-900">Revoke Page Access?</h3>
              <p className="text-xs text-gray-500 mt-1">
                This employee will no longer have custom page access and will revert to default permissions.
              </p>
            </div>
            <div className="flex gap-2.5 pt-2">
              <button
                type="button"
                onClick={() => setDeleteConfirmId(null)}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-xs font-bold uppercase tracking-wider text-gray-600 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  const target = allAccessData.find((a) => a._id === deleteConfirmId);
                  if (target) handleDeleteAccess(target);
                }}
                className="flex-1 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold uppercase tracking-wider transition-colors shadow-md shadow-rose-600/20"
              >
                Yes, Revoke
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PageAccess;